/**
 * What the overlays say and where they go, worked out from the plot's geometry and nothing else.
 *
 * Pure: no DOM, no Angular. The component hands it the geometry (plot-geometry.ts) and a way to
 * measure text, and draws whatever comes back. That is what lets every edge case that has reached
 * production (an empty bucket, a week of zeros, a single bar, 730 days, 18 meters) be tested in a
 * spec rather than found by a client.
 *
 * The label rules are the ones Jayden signed off, unchanged; only where the boxes come from changed.
 */
import { formatNumber, formatValue, labelShown, ReportKind } from './format';
import { Box, GeoBucket, PlotGeometry } from './plot-geometry';
import { ChartGuide } from './chart-guides';
import { formatDate } from './periods';

/** A number to draw. Coordinates are its centre, in the chart svg's space. */
export interface LabelOut {
  text: string;
  x: number;
  y: number;
  /** The extent along the column (its height flat, its width turned), for the tests and leader lines. */
  along: number;
  turned: boolean;
  /** Set when the number was shrunk to fit its slot. */
  fontSize?: number;
  /** Outside its segment, on the plot's white. */
  outside: boolean;
  /** On a tag in its segment's colour. */
  tag: boolean;
  /** Dark text, on a light fill. */
  dark: boolean;
  /** A busy column's total. */
  total: boolean;
  fill?: string;
  series?: string;
  /** How far it was pulled off its segment's middle, when it was. */
  pulled?: number;
}

/** The thin line from a pulled tag back to its segment. */
export interface LeaderOut {
  x: number;
  top: number;
  height: number;
}

/** A bracket under the axis naming the month (or day) its bars fall in. */
export interface BandOut {
  left: number;
  width: number;
  label: string;
}

/** A what-if guide: a dashed level, or a shaded run of hours. */
export interface GuideOut {
  kind: 'level' | 'hours';
  box: Box;
  label: string;
  warm?: boolean;
}

export interface LabelOptions {
  kind: ReportKind;
  averaged: boolean;
  /** Three or more meters stacked: numbers only where they fit, plus each column's total. */
  busyStack: boolean;
  /** Each series' colour, by name. */
  fills: Map<string, string>;
  /** Width of a text at the label font. */
  measure: (text: string) => number;
  /** The label font's size and line height, px. */
  fontSize: number;
  lineHeight: number;
  /** The overlay's full height, for a tag with no room on the plot. */
  boxHeight: number;
}

/**
 * True when dark text reads better than white on this fill: whichever has the higher WCAG contrast.
 * White on a mid blue (#418cf0) is 3.4:1 against 5.6:1 for dark, so mid colours get dark text now,
 * as Highcharts' "contrast" label colour does.
 */
export function isLightFill(fill: string | null | undefined): boolean {
  const s = (fill || '').trim();
  const hex = /^#?([0-9a-f]{6})$/i.exec(s);
  const rgb = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/i.exec(s);
  let r: number, g: number, b: number;
  if (hex) {
    const n = parseInt(hex[1], 16);
    r = (n >> 16) & 255; g = (n >> 8) & 255; b = n & 255;
  } else if (rgb) {
    r = +rgb[1]; g = +rgb[2]; b = +rgb[3];
  } else {
    return false;
  }
  const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // Contrast against white is 1.05 / (L + 0.05); against #111827 (L 0.0116) it is (L + 0.05) / 0.0616.
  return (lum + 0.05) / 0.0616 > 1.05 / (lum + 0.05);
}

/**
 * The numbers on the bars, the way the SSRS report shows them, placed so none sits on another.
 *
 * Every point the report's rule labels (labelShown) gets its number: inside its segment when it fits,
 * turned up the bar when it is wider than the bar's slot, and on a tag in the segment's colour when it
 * does not fit inside at all (Jayden, 09/10/26: every figure must be visible). A tag may be pulled
 * along its column to the nearest free spot, keeping a thin line back to its segment. On a busy stack
 * a segment too small for its number gets none; the column's total and the hover panel cover it.
 */
export function layoutLabels(buckets: GeoBucket[], geo: PlotGeometry, opt: LabelOptions): { labels: LabelOut[]; leaders: LeaderOut[] } {
  const plotTop = geo.plot.top;
  const plotBottom = geo.plot.top + geo.plot.height;

  interface Item { r: Box; v: number; cx: number; name: string; fill: string; total?: boolean }
  const items: Item[] = [];

  buckets.forEach((b, i) => {
    b.segments.forEach((s, k) => {
      const r = geo.bars[i]?.[k];
      if (!r || r.width <= 0 || !labelShown(opt.kind, s.value)) return;
      items.push({ r, v: s.value, cx: r.left + r.width / 2, name: s.name, fill: opt.fills.get(s.name) ?? '' });
    });
  });

  // Each busy column's totals, up and down, as items that only ever sit outside.
  if (opt.busyStack) {
    buckets.forEach((b, i) => {
      for (const up of [true, false]) {
        let sum = 0, top = Infinity, bottom = -Infinity;
        b.segments.forEach((s, k) => {
          if (!s.value || (s.value > 0) !== up) return;
          const r = geo.bars[i][k];
          sum += s.value;
          top = Math.min(top, r.top);
          bottom = Math.max(bottom, r.top + r.height);
        });
        if (!(Math.abs(sum) > 1)) continue;
        const c = geo.columns[i];
        items.push({ r: { left: c.x0, top, width: c.x1 - c.x0, height: bottom - top }, v: sum, cx: (c.x0 + c.x1) / 2, name: '', fill: '', total: true });
      }
    });
  }
  if (!items.length) return { labels: [], leaders: [] };

  // The room a number has across the axis is its bar's slot: the distance to the next bar along.
  const xs = Array.from(new Set(items.map(it => Math.round(it.cx)))).sort((a, b) => a - b);
  const roomAt = new Map<number, number>();
  xs.forEach((x, k) => {
    const step = Math.min(k > 0 ? x - xs[k - 1] : Infinity, k < xs.length - 1 ? xs[k + 1] - x : Infinity);
    roomAt.set(x, step === Infinity ? geo.plot.width : step);
  });

  const columnEnd = (x: number, up: boolean) => {
    const inColumn = items.filter(it => Math.round(it.cx) === x && (it.v > 0) === up);
    return up ? Math.min(...inColumn.map(it => it.r.top)) : Math.max(...inColumn.map(it => it.r.top + it.r.height));
  };
  const outsideAt = new Map<string, number>();

  interface Placed { label: LabelOut; x: number; segY: number; moves: boolean }
  const placed: Placed[] = [];

  for (const it of items) {
    const x = Math.round(it.cx);
    const room = roomAt.get(x)!;
    const text = it.total ? formatNumber(it.v, 0) : formatValue(it.v, opt.kind, opt.averaged);
    // A column's total is bold and on a pill (.rsc-col-total: weight 700, 3px a side), wider than the
    // label font measures; counted, or two neighbouring totals ran into each other.
    let textW = Math.ceil(opt.measure(text) * (it.total ? 1.08 : 1)) + (it.total ? 6 : 0);
    let textH = opt.lineHeight;
    let fontSize: number | undefined;

    // A number that will not fit inside its segment flat goes on a tag, whose padding (3px a side)
    // widens it; counted here, or a flat tag runs into the next column's.
    // A flat number needs its box and a pixel clear either side. The box is the em at line-height 1,
    // and Edge measures 11px digits at 10px of it, so 13px leaves the ink 1.5px of segment above and
    // below. It asked 2px a side until 14/10/26, and a laptop's 14px segments went blank where the
    // number plainly fitted (Unit 3, 17 Aug and 21 Sept; Jayden).
    const clear = 2;
    const flatInside = !it.total && it.r.height >= textH + clear && it.r.width >= textW;
    const tagPad = !it.total && !opt.busyStack && !flatInside ? 6 : 0;
    const turned = textW + tagPad > room - 2;
    if (turned && room - 1 < textH) {
      // Turned and still too wide across: shrink to the slot, down to 8px. Narrower, nothing is legible.
      if (room < 8) continue;
      fontSize = Math.floor(room - 1);
      textW = Math.ceil(textW * fontSize / opt.fontSize);
      textH = fontSize;
    }
    const along = turned ? textW : textH;
    const across = turned ? textH : textW;

    const top = it.r.top;
    const bottom = it.r.top + it.r.height;
    const up = it.v > 0;

    // A turned number may be a pixel or two wider than its bar: it is sized to the slot.
    let inside = !it.total && it.r.height >= along + (turned ? 4 : clear) && it.r.width + (turned ? 2 : 0) >= across;
    if (!inside && opt.busyStack && !it.total) continue;

    let y: number;
    let snug = false;
    if (!inside && !it.total) {
      snug = true;
      y = top + (bottom - top) / 2;
    } else if (!inside) {
      // A column's total past the end of the column, as SSRS moves one that does not fit.
      const key = x + (up ? '+' : '-');
      const from = outsideAt.get(key) ?? columnEnd(x, up);
      y = up ? from - 2 - along / 2 : from + 2 + along / 2;
      const clear = up ? y - along / 2 >= plotTop : y + along / 2 <= plotBottom;
      if (clear) outsideAt.set(key, up ? from - 2 - along : from + 2 + along);
      else y = up ? plotTop + along / 2 + 1 : plotBottom - along / 2 - 1;
    } else {
      y = top + (bottom - top) / 2;
    }

    const tag = !inside && !it.total && !!it.fill;
    placed.push({
      x,
      segY: top + (bottom - top) / 2,
      moves: snug,
      label: {
        text, x: it.cx, y, turned, fontSize,
        along: along + (snug && turned ? tagPad : 0),
        outside: !inside && !snug,
        tag,
        dark: (inside || tag) && isLightFill(it.fill),
        total: !!it.total,
        fill: tag ? it.fill : undefined,
        series: it.total ? undefined : it.name
      }
    });
  }

  // Pull a column's tags apart where they would sit on each other or on a number inside its segment.
  // Numbers inside never move; each tag, in order down the column, takes the free spot nearest its
  // own segment, above or below. A spot on the plot is preferred, then anywhere in the box.
  const byColumn = new Map<number, Placed[]>();
  for (const p of placed) {
    const list = byColumn.get(p.x);
    if (list) list.push(p); else byColumn.set(p.x, [p]);
  }
  const gap = 1;
  const clash = (y: number, along: number, taken: { y: number; along: number }[]) =>
    taken.some(t => Math.abs(y - t.y) < (along + t.along) / 2 + gap);
  for (const list of byColumn.values()) {
    if (!list.some(p => p.moves)) continue;
    const taken = list.filter(p => !p.moves).map(p => ({ y: p.label.y, along: p.label.along }));
    for (const p of list.filter(q => q.moves).sort((a, b) => a.label.y - b.label.y)) {
      const along = p.label.along;
      const fits = (y: number, lo: number, hi: number) => y - along / 2 >= lo && y + along / 2 <= hi && !clash(y, along, taken);
      let best: number | null = null;
      for (const [lo, hi] of [[plotTop, plotBottom], [0, opt.boxHeight]]) {
        for (let d = 0; d <= opt.boxHeight && best === null; d++) {
          if (fits(p.label.y + d, lo, hi)) best = p.label.y + d;
          else if (fits(p.label.y - d, lo, hi)) best = p.label.y - d;
        }
        if (best !== null) break;
      }
      if (best !== null) p.label.y = best;
      taken.push({ y: p.label.y, along });
    }
  }

  const leaders: LeaderOut[] = [];
  for (const p of placed) {
    const l = p.label;
    if (!p.moves) continue;
    const off = l.y - p.segY;
    if (Math.abs(off) >= 1) l.pulled = Math.round(off);
    if (Math.abs(off) > l.along / 2 + 1) {
      const edge = l.y > p.segY ? l.y - l.along / 2 : l.y + l.along / 2;
      leaders.push({ x: l.x, top: Math.min(p.segY, edge), height: Math.abs(edge - p.segY) });
    }
  }

  return { labels: placed.map(p => p.label), leaders };
}

/**
 * Brackets under the axis, one per run of buckets in the same month (or, under hours, the same day),
 * from the first bar of the run to the last. Every bucket has its place from the scale, so an empty
 * one is spanned like any other.
 *
 * Each bracket stays inside the plot: a line's first and last points sit on the plot's edges, and
 * their half-way slots reached past them, so the last month's label was cut off at the edge ("Sept
 * 20", 14/10/26). Given a way to measure text, a label too wide for its bracket is shortened, then
 * dropped, the way Highcharts drops an axis label that would not fit rather than letting it spill.
 * The bracket itself is always drawn.
 */
export function layoutBands(dates: (Date | null)[], geo: PlotGeometry, by: 'month' | 'day',
                            measure?: (text: string) => number): BandOut[] {
  const lo = geo.plot.left, hi = geo.plot.left + geo.plot.width;
  const bands: { key: string; left: number; right: number; date: Date }[] = [];
  dates.forEach((d, i) => {
    const c = geo.columns[i];
    if (!d || !c) return;
    const key = by === 'month' ? `${d.getFullYear()}-${d.getMonth()}` : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const left = Math.max(lo, c.x0), right = Math.min(hi, c.x1);
    const last = bands[bands.length - 1];
    if (last && last.key === key) {
      last.right = right;
      return;
    }
    bands.push({ key, left, right, date: d });
  });
  return bands.map(({ left, right, date }) => {
    const width = right - left;
    const names = by === 'month'
      ? [formatDate(date, { month: 'short', year: 'numeric' }), formatDate(date, { month: 'short' })]
      : [formatDate(date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }), formatDate(date, { weekday: 'short', day: 'numeric' })];
    // The label's own padding, 4px each side (.rsc-month-band-label).
    const label = !measure ? names[0] : names.find(n => measure(n) + 8 <= width) ?? '';
    return { left, width, label };
  });
}

/**
 * The what-if's guides: a dashed line at a level across the bars, and a shade behind each unbroken run
 * of hours a rate covers, out to the middle of the gap either side so it reads as a stretch of time.
 */
export function layoutGuides(guides: ChartGuide[], dates: (Date | null)[], geo: PlotGeometry, hourly: boolean): GuideOut[] {
  const out: GuideOut[] = [];
  const cols = geo.columns;
  if (!cols.length) return out;
  const plotTop = geo.plot.top, plotBottom = geo.plot.top + geo.plot.height;

  for (const g of guides) {
    if (g.kind === 'level') {
      const y = geo.y(g.value);
      if (!isFinite(y) || y < plotTop - 1 || y > plotBottom + 1) continue;
      out.push({ kind: 'level', label: g.label, box: { left: cols[0].x0, top: y, width: cols[cols.length - 1].x1 - cols[0].x0, height: 0 } });
    } else if (hourly) {
      // An hour is inside when it falls in [from, to), wrapping past midnight when from is later.
      const covers = (h: number) => g.fromHour < g.toHour ? h >= g.fromHour && h < g.toHour : h >= g.fromHour || h < g.toHour;
      let run: { left: number; right: number } | null = null;
      dates.forEach((d, i) => {
        const inside = !!d && covers(d.getHours());
        if (inside) {
          if (run) run.right = cols[i].slot1;
          else run = { left: cols[i].slot0, right: cols[i].slot1 };
        }
        if ((!inside || i === dates.length - 1) && run) {
          out.push({ kind: 'hours', label: g.label, warm: g.tone === 'warm', box: { left: run.left, top: plotTop, width: run.right - run.left, height: plotBottom - plotTop } });
          run = null;
        }
      });
    }
  }
  return out;
}
